'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import type { CSSProperties } from 'react'
import type { AtlasSpecies } from './atlas-data'
import { trackEvent } from '@/lib/analytics'
import { creditFromPhotoRecord, imageCreditTitle } from '@/lib/image-credits'
import { ImageCreditsFooter } from '@/components/ui/image-credits-footer'
import {
  ArrowLeftRight,
  ArrowRight,
  BookOpenText,
  Camera,
  Check,
  ChevronDown,
  CircleHelp,
  Eye,
  EyeOff,
  Maximize2,
  Minimize2,
  ShieldCheck,
  Sprout,
  X,
} from 'lucide-react'
import {
  KENTUCKY_BLUEGRASS_MODEL_CUES,
  KENTUCKY_BLUEGRASS_PART_LABELS,
} from './kentucky-bluegrass-model'
import {
  getAtlasMorphology,
  type AtlasAnatomyPart,
  type AtlasMorphologyProfile,
} from '@/data/grass-atlas-morphology'
import { KentuckyBluegrassViewer } from './kentucky-bluegrass-viewer'
import { ProceduralGrassLoader } from './models/procedural-grass-loader'
import { GrassSimulationPanel } from './grass-simulation-panel'
import {
  getGrassSimulationState,
  getMatureHealthyState,
  SIMULATION_OPENING_DAY,
  type GrassSimulationInput,
  type GrassSimulationPreset,
  type GrassSimulationVisualState,
} from './grass-simulation'
import styles from './grass-atlas.module.css'

type AtlasMode = 'explore' | 'compare' | 'quiz' | 'simulate'
export type AnatomyPart = AtlasAnatomyPart

const PARTS: Record<
  AnatomyPart,
  { label: string; kicker: string; description: string }
> = {
  'blade-tip': {
    label: 'Blade tip',
    kicker: 'What to notice',
    description:
      'The very end of a blade can look pointed, round, or like the front of a tiny boat. This small shape helps tell grasses apart.',
  },
  blade: {
    label: 'Leaf blade',
    kicker: 'How it feels',
    description:
      'Look at how the newest leaf starts, folded flat or rolled like a straw. Then check width, lines, hairs, and shine.',
  },
  collar: {
    label: 'Collar region',
    kicker: 'Look closely',
    description:
      'This is where the blade meets the stem. Bend the blade back to find a tiny flap called the ligule, and small side claws called auricles.',
  },
  node: {
    label: 'Node & sheath',
    kicker: 'How it grows',
    description:
      'Nodes are the joints where leaves and new shoots start. The sheath is the sleeve that wraps the stem below the blade.',
  },
  runner: {
    label: 'Runner',
    kicker: 'How it spreads',
    description:
      'Runners show how grass moves across the ground. Stolons grow on top, rhizomes grow below, and bunch grasses stay in clumps.',
  },
  roots: {
    label: 'Root system',
    kicker: 'Below the soil',
    description:
      'Fibrous roots hold the plant in place and gather water. Some grasses also have underground runners called rhizomes.',
  },
  seedhead: {
    label: 'Seedhead',
    kicker: 'Big ID clue',
    description:
      'The seedhead can be one of the best clues. Look at its overall shape and how the small seeds connect to the branches.',
  },
}

const PART_SHORT_LABELS: Record<AnatomyPart, string> = {
  seedhead: 'Seedhead',
  'blade-tip': 'Blade tip',
  blade: 'Blade',
  collar: 'Collar',
  node: 'Stem',
  runner: 'Runner',
  roots: 'Roots',
}

const MODE_TABS = [
  { id: 'explore', label: 'Look around', icon: Eye },
  { id: 'compare', label: 'Compare', icon: ArrowLeftRight },
  { id: 'quiz', label: 'Quiz', icon: CircleHelp },
  { id: 'simulate', label: 'Grow it', icon: Sprout },
] as const

function ModeTabs({
  mode,
  className,
  onModeChange,
}: {
  mode: AtlasMode
  className: string
  onModeChange: (mode: AtlasMode) => void
}) {
  return (
    <div className={`${styles.modeTabs} ${className}`} aria-label="Learning mode">
      {MODE_TABS.map(({ id, label, icon: ModeIcon }) => (
        <button
          type="button"
          key={id}
          onClick={() => onModeChange(id)}
          className={mode === id ? styles.modeActive : ''}
          aria-pressed={mode === id}
        >
          <ModeIcon size={15} aria-hidden="true" />
          {label}
        </button>
      ))}
    </div>
  )
}

const PART_POSITIONS: Array<{ id: AnatomyPart; x: number; y: number }> = [
  { id: 'seedhead', x: 54, y: 15 },
  { id: 'blade-tip', x: 72, y: 25 },
  { id: 'blade', x: 31, y: 39 },
  { id: 'collar', x: 54, y: 51 },
  { id: 'node', x: 61, y: 61 },
  { id: 'runner', x: 31, y: 67 },
  { id: 'roots', x: 56, y: 84 },
]

function normalizeSeasonName(season: AtlasSpecies['season']) {
  return season === 'cool' ? 'Cool season' : 'Warm season'
}

function anatomyCue(species: AtlasSpecies, profile: AtlasMorphologyProfile, part: AnatomyPart) {
  switch (part) {
    case 'blade-tip': return profile.bladeTipCue
    case 'blade': return `${profile.vernation} Mature blades are ${species.bladeWidth.toLowerCase()} and ${species.texture.toLowerCase()}.`
    case 'collar': return profile.collarCue
    case 'node': return profile.nodeCue
    case 'runner': return profile.runnerCue
    case 'roots': return profile.rootCue
    case 'seedhead': return species.seedhead
  }
}

function specimenVariant(species: AtlasSpecies) {
  return getAtlasMorphology(species.slug).visual
}

function SeedheadGlyph({ form }: { form: AtlasMorphologyProfile['visual']['seedhead'] }) {
  if (form === 'hidden') {
    return (
      <g className={styles.seedhead}>
        <path d="M374 290 C375 240 373 207 378 172" />
        <path d="M377 188 C362 176 352 169 340 160 M378 191 C393 178 402 169 414 158" />
      </g>
    )
  }

  if (form === 'digitate' || form === 'y-fork') {
    const arms = form === 'y-fork'
      ? ['M380 116 L318 61', 'M380 116 L444 61']
      : ['M380 116 L306 79', 'M380 116 L333 52', 'M380 116 L381 41', 'M380 116 L430 53', 'M380 116 L457 82']
    return (
      <g className={styles.seedhead}>
        <path d="M374 290 C375 214 377 160 380 116" />
        {arms.map((d) => <path d={d} key={d} />)}
      </g>
    )
  }

  if (form === 'spike' || form === 'awned-spike' || form === 'raceme') {
    return (
      <g className={styles.seedhead}>
        <path d="M374 290 C375 214 377 137 380 55" />
        {[82, 108, 134, 160].map((y, index) => (
          <g key={y}>
            <ellipse cx={index % 2 ? 395 : 366} cy={y} rx={form === 'raceme' ? 7 : 10} ry="17" transform={`rotate(${index % 2 ? 42 : -42} ${index % 2 ? 395 : 366} ${y})`} />
            {form === 'awned-spike' ? <path d={`M${index % 2 ? 400 : 360} ${y - 8} L${index % 2 ? 421 : 338} ${y - 28}`} /> : null}
          </g>
        ))}
      </g>
    )
  }

  if (form === 'buffalo') {
    return (
      <g className={styles.seedhead}>
        <path d="M374 290 C375 220 377 166 380 105" />
        <path d="M380 114 L342 85 M380 127 L420 96" />
        <circle cx="340" cy="84" r="12" />
        <circle cx="421" cy="95" r="12" />
      </g>
    )
  }

  if (form === 'mixed') {
    return (
      <g className={styles.seedhead}>
        <path d="M354 290 C355 220 349 160 350 88 M382 290 C384 219 388 145 390 64 M410 290 C408 226 423 171 442 104" />
        <path d="M350 108 L324 87 M350 121 L376 98 M390 92 L373 78 M390 108 L408 90 M442 105 L420 79 M442 105 L465 83" />
      </g>
    )
  }

  return (
    <g className={styles.seedhead}>
      <path d="M374 290 C375 216 373 142 383 66" />
      <path d="M382 92 L343 67 M381 105 L418 76 M379 121 L342 102 M378 138 L420 111 M376 156 L339 135" />
      <ellipse cx="340" cy="65" rx="8" ry="18" transform="rotate(-52 340 65)" />
      <ellipse cx="420" cy="75" rx="8" ry="19" transform="rotate(50 420 75)" />
      <ellipse cx="339" cy="101" rx="8" ry="18" transform="rotate(-54 339 101)" />
      <ellipse cx="422" cy="109" rx="8" ry="18" transform="rotate(54 422 109)" />
    </g>
  )
}

function AtlasMark({ species }: { species: AtlasSpecies }) {
  const monograms: Record<string, string> = {
    'kentucky-bluegrass': 'Pp',
    'tall-fescue': 'Fa',
    'fine-fescue': 'Fs',
    'perennial-ryegrass': 'Lp',
    'annual-ryegrass': 'Lm',
    'mixed-cool-season-blend': 'Mx',
    bermudagrass: 'Cd',
    kikuyugrass: 'Cc',
    zoysiagrass: 'Zs',
    'st-augustinegrass': 'Ss',
    centipede: 'Eo',
    bahiagrass: 'Pn',
    buffalograss: 'Bd',
  }
  const fallback = species.name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
  return (
    <span className={styles.speciesMark} aria-hidden="true">
      <span>{monograms[species.slug] ?? fallback}</span>
    </span>
  )
}

function AtlasReferencePhoto({ species }: { species: AtlasSpecies }) {
  const [activeIndex, setActiveIndex] = useState(0)
  const active = species.referenceImages[activeIndex] ?? species.referenceImages[0]

  useEffect(() => {
    setActiveIndex(0)
  }, [species.slug])

  if (!active) {
    return (
      <div className={styles.referenceUnavailable}>
        <Camera size={17} />
        <p><strong>No exact reference photo yet</strong>We will not substitute a different species or an unverified lawn photo.</p>
      </div>
    )
  }

  const activeCredit = creditFromPhotoRecord(active)

  return (
    <figure className={styles.referencePhoto}>
      <ImageCreditsFooter credits={species.referenceImages.map(creditFromPhotoRecord)} />
      <div className={styles.referencePhotoFrame}>
        <Image
          src={active.src}
          alt={`Reference photo of ${species.name}${active.caption ? `, ${active.caption}` : ''}`}
          title={activeCredit ? imageCreditTitle(activeCredit) : undefined}
          fill
          unoptimized
          sizes="(max-width: 720px) 44vw, 280px"
        />
      </div>
      <figcaption>
        <strong>{active.caption ?? `Reference: ${species.name}`}</strong>
      </figcaption>
      {species.referenceImages.length > 1 ? (
        <div className={styles.referenceThumbs} aria-label={`${species.name} reference views`}>
          {species.referenceImages.map((image, index) => (
            <button
              type="button"
              key={image.src}
              aria-label={image.caption ?? `Reference view ${index + 1}`}
              aria-pressed={index === activeIndex}
              onClick={() => {
                setActiveIndex(index)
                trackEvent('grass_atlas_reference_viewed', { species: species.slug, image_index: index })
              }}
            >
              <Image src={image.src} alt="" fill unoptimized sizes="42px" />
            </button>
          ))}
        </div>
      ) : null}
    </figure>
  )
}

function IllustratedSpecimen({
  species,
  activePart,
  onPartChange,
  dissected,
  showNumbers,
}: {
  species: AtlasSpecies
  activePart: AnatomyPart
  onPartChange: (part: AnatomyPart) => void
  dissected: boolean
  showNumbers: boolean
}) {
  const variant = specimenVariant(species)

  return (
    <div className={`${styles.specimenStage} ${dissected ? styles.dissected : ''}`}>
      <div className={styles.scaleNote} aria-hidden="true">
        <span />
        diagnostic specimen
      </div>

      <svg
        className={styles.specimen}
        viewBox="0 0 720 720"
        role="img"
        aria-label={`Illustrated ${species.name} specimen showing leaves, stem, runners, roots, and seedhead`}
      >
        <defs>
          <linearGradient id="bladeGreen" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={species.season === 'cool' ? '#6b9b55' : '#8aaa3c'} />
            <stop offset="0.52" stopColor={species.season === 'cool' ? '#2f6843' : '#487328'} />
            <stop offset="1" stopColor="#173f2d" />
          </linearGradient>
          <linearGradient id="rootOchre" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#92703f" />
            <stop offset="1" stopColor="#d4b878" stopOpacity="0.45" />
          </linearGradient>
          <filter id="plantShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="14" stdDeviation="13" floodColor="#173f2d" floodOpacity="0.18" />
          </filter>
        </defs>

        <g className={styles.specimenRoot} filter="url(#plantShadow)">
          <SeedheadGlyph form={variant.seedhead} />

          <g
            className={`${styles.blades} ${
              variant.blade === 'broad'
                ? styles.broadBlades
                : variant.blade === 'needle'
                  ? styles.needleBlades
                  : variant.blade === 'fine'
                    ? styles.fineBlades
                    : variant.blade === 'mixed'
                      ? styles.mixedBlades
                      : ''
            } ${variant.posture === 'prostrate' ? styles.prostrateBlades : ''} ${variant.posture === 'tufted' ? styles.tuftedBlades : ''} ${variant.surface === 'hairy' ? styles.hairyBlades : ''} ${variant.surface === 'ridged' ? styles.ridgedBlades : ''} ${variant.surface === 'glossy' ? styles.glossyBlades : ''}`}
            fill="url(#bladeGreen)"
          >
            <path d="M358 432 C309 352 269 222 295 116 C332 226 355 332 365 433 Z" />
            <path d="M371 430 C373 301 390 174 455 92 C439 213 413 338 381 433 Z" />
            <path d="M348 434 C286 387 215 299 188 202 C253 275 322 361 360 431 Z" />
            <path d="M387 433 C425 361 488 295 551 251 C513 338 445 404 393 439 Z" />
            <path d="M350 437 C300 426 228 409 168 357 C248 381 315 402 368 430 Z" />
            <path d="M387 439 C448 420 510 399 574 351 C534 414 451 446 389 448 Z" />
            {variant.growth !== 'bunch' ? <path d="M365 440 C340 311 337 206 356 115 C379 242 384 345 377 441 Z" /> : null}
          </g>

          <g className={styles.stems}>
            <path d="M357 427 C360 381 360 329 354 283" />
            <path d="M378 432 C380 380 379 330 374 284" />
            <path d="M366 430 L366 289" />
            <ellipse cx="366" cy="367" rx="17" ry="6" />
            <ellipse cx="365" cy="426" rx="23" ry="8" />
          </g>

          <g className={`${styles.runners} ${['stolon', 'both', 'mixed'].includes(variant.growth) ? styles.runnersVisible : ''}`}>
            <path d="M365 446 C287 455 217 468 129 448" />
            <path d="M364 450 C446 454 516 477 605 455" />
            <path d="M216 460 C214 429 224 411 243 394" />
            <path d="M518 467 C521 436 512 415 494 398" />
            <circle cx="215" cy="460" r="7" />
            <circle cx="519" cy="467" r="7" />
          </g>

          <g className={styles.crown}>
            <path d="M337 433 C350 419 384 417 400 435 L395 467 C378 479 349 477 334 464 Z" />
          </g>

          <g className={styles.roots} stroke="url(#rootOchre)">
            <path d="M348 459 C314 513 316 587 283 668" />
            <path d="M359 464 C350 530 361 602 342 688" />
            <path d="M372 464 C383 533 370 606 391 688" />
            <path d="M387 460 C424 515 416 599 458 668" />
            <path d="M336 499 C310 516 294 538 277 566 M351 549 C323 566 309 590 297 619" />
            <path d="M384 507 C414 525 431 546 446 576 M379 564 C411 581 426 605 438 632" />
            <path d="M363 484 C336 507 331 527 324 548 M371 535 C396 553 404 575 412 598" />
          </g>
          <path
            className={styles.rhizome}
            data-visible={['rhizome', 'both', 'bunch-rhizome', 'mixed'].includes(variant.growth)}
            d="M287 522 C333 493 397 500 446 526"
          />
        </g>

        <g className={styles.soilLine}>
          <path d="M84 455 C194 445 276 462 364 453 C467 442 548 466 640 451" />
          <text x="88" y="486">SOIL LINE</text>
        </g>
      </svg>

      <div className={styles.hotspots} hidden={!showNumbers}>
        {PART_POSITIONS.map(({ id, x, y }, index) => (
          <button
            type="button"
            key={id}
            className={`${styles.hotspot} ${activePart === id ? styles.hotspotActive : ''}`}
            style={{ left: `${x}%`, top: `${y}%` }}
            onClick={() => onPartChange(id)}
            aria-label={`Explore ${PARTS[id].label}`}
            aria-pressed={activePart === id}
          >
            <span>{index + 1}</span>
          </button>
        ))}
      </div>

      <div className={styles.specimenCaption}>
        <span>{species.name}</span>
        <span>{showNumbers ? 'Tap a marker to inspect' : 'Choose a grass part beside the model'}</span>
      </div>
    </div>
  )
}

function Specimen({
  species,
  activePart,
  onPartChange,
  dissected,
  simulation,
  simulationActive,
  showNumbers,
}: {
  species: AtlasSpecies
  activePart: AnatomyPart
  onPartChange: (part: AnatomyPart) => void
  dissected: boolean
  simulation: GrassSimulationVisualState
  simulationActive: boolean
  showNumbers: boolean
}) {
  const [modelFailure, setModelFailure] = useState<{
    slug: string
    message: string
    stage: string
  } | null>(null)
  const threeUnavailable = modelFailure?.slug === species.slug
  const handleThreeUnavailable = useCallback((error: unknown, stage: string) => {
    const rawMessage = error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : 'Unknown 3D renderer error'
    const message = rawMessage.slice(0, 240)
    setModelFailure({ slug: species.slug, message, stage })
    trackEvent('grass_atlas_model_failed', {
      species: species.slug,
      failure_stage: stage,
      failure_message: message,
    })
  }, [species.slug])
  const retryThreeModel = useCallback(() => {
    setModelFailure(null)
    trackEvent('grass_atlas_model_retry', { species: species.slug })
  }, [species.slug])
  const handleModelReady = useCallback((durationMs: number) => {
    const roundedDuration = Math.round(durationMs)
    performance.mark(`grass-atlas-model-ready:${species.slug}`)
    trackEvent('grass_atlas_model_ready', {
      species: species.slug,
      model_ready_ms: roundedDuration,
      navigation_ready_ms: Math.round(performance.now()),
    })
  }, [species.slug])
  if (species.slug === 'kentucky-bluegrass' && !threeUnavailable) {
    return (
      <KentuckyBluegrassViewer
        activePart={activePart}
        dissected={dissected}
        onPartChange={onPartChange}
        onUnavailable={handleThreeUnavailable}
        onReady={handleModelReady}
        simulation={simulation}
        simulationActive={simulationActive}
        showNumbers={showNumbers}
      />
    )
  }

  if (!threeUnavailable) {
    return (
      <ProceduralGrassLoader
        slug={species.slug}
        activePart={activePart}
        dissected={dissected}
        onPartChange={onPartChange}
        onUnavailable={handleThreeUnavailable}
        onReady={handleModelReady}
        simulation={simulation}
        simulationActive={simulationActive}
        showNumbers={showNumbers}
      />
    )
  }

  return (
    <div className={styles.modelFallback}>
      <IllustratedSpecimen
        species={species}
        activePart={activePart}
        onPartChange={onPartChange}
        dissected={dissected}
        showNumbers={showNumbers}
      />
      <div className={styles.modelFallbackNotice} role="status">
        <strong>The 3D grass did not load</strong>
        <span>
          You can still tap the numbers on this picture, or try the 3D view again.
        </span>
        <button type="button" onClick={retryThreeModel}>
          Try 3D again
        </button>
      </div>
    </div>
  )
}

function ExplorePanel({
  species,
  activePart,
  onPartChange,
  showNumbers,
}: {
  species: AtlasSpecies
  activePart: AnatomyPart
  onPartChange: (part: AnatomyPart) => void
  showNumbers: boolean
}) {
  const isKentuckyBluegrass = species.slug === 'kentucky-bluegrass'
  const profile = getAtlasMorphology(species.slug)
  const part = {
    ...PARTS[activePart],
    label: isKentuckyBluegrass
      ? KENTUCKY_BLUEGRASS_PART_LABELS[activePart] ?? PARTS[activePart].label
      : PARTS[activePart].label,
  }

  return (
    <aside className={styles.detailPanel} aria-live="polite" aria-label={`${species.name} identification notes`}>
      <div className={styles.detailHeader}>
        <div>
          <span className={styles.eyebrow}>{normalizeSeasonName(species.season)}</span>
          <h2>{species.name}</h2>
          <em>{species.zones}</em>
        </div>
        <AtlasMark species={species} />
      </div>

      <p className={styles.anatomyPrompt}>
        {showNumbers ? 'Tap a number on the grass, or choose a part here.' : 'Choose a grass part here, or show the numbers on the model.'}
      </p>
      <div className={styles.anatomyIndex} role="group" aria-label="Grass parts">
        {PART_POSITIONS.map(({ id }, index) => (
          <button
            type="button"
            key={id}
            title={PART_SHORT_LABELS[id]}
            aria-label={`Inspect ${PARTS[id].label}`}
            aria-pressed={activePart === id}
            className={activePart === id ? styles.anatomyIndexActive : ''}
            onClick={() => onPartChange(id)}
          >
            <span>{index + 1}</span>
            {PART_SHORT_LABELS[id]}
          </button>
        ))}
      </div>

      <div className={styles.selectedPart}>
        <span>{part.kicker}</span>
        <h3>{part.label}</h3>
        <p>{part.description}</p>
        <p className={styles.specimenCue}>
          <strong>On this grass</strong>
          {isKentuckyBluegrass ? KENTUCKY_BLUEGRASS_MODEL_CUES[activePart] : anatomyCue(species, profile, activePart)}
        </p>
      </div>

      <details className={styles.referenceDetails}>
        <summary>Species details and sources</summary>
        <div className={styles.referenceDetailsBody}>
          <p className={styles.summary}>{species.summary}</p>

          <div className={styles.fieldReferenceBlock}>
            <strong>Real photo</strong>
            <AtlasReferencePhoto species={species} />
          </div>

          <div className={styles.fingerprint}>
            <span>Quick way to spot it</span>
            <ul>
              {profile.fingerprint.map((cue) => <li key={cue}>{cue}</li>)}
            </ul>
          </div>

          {species.photoIdCues.length ? (
            <div className={styles.compactChecklist}>
              <strong>Photos worth taking</strong>
              <ul>{species.photoIdCues.slice(0, 3).map((cue) => <li key={cue}>{cue}</li>)}</ul>
            </div>
          ) : null}

          {species.modelLimitations.length ? (
            <div className={styles.compactChecklist}>
              <strong>What this model does not show</strong>
              <ul>{species.modelLimitations.slice(0, 3).map((limitation) => <li key={limitation}>{limitation}</li>)}</ul>
            </div>
          ) : null}

          <dl className={styles.facts}>
            <div>
              <dt>Scientific name</dt>
              <dd><em>{profile.scientificName}</em></dd>
            </div>
            <div>
              <dt>Blade</dt>
              <dd>{species.bladeWidth}</dd>
            </div>
            <div>
              <dt>Tip & shape</dt>
              <dd>{species.bladeShape}</dd>
            </div>
            <div>
              <dt>Surface</dt>
              <dd>{species.texture}</dd>
            </div>
            <div>
              <dt>Spreads by</dt>
              <dd>{species.growthHabit}</dd>
            </div>
          </dl>

          <div className={styles.sourceLinks}>
            <strong>Checked against</strong>
            {species.modelSources.map((source) => (
              <a key={source.href} href={source.href} target="_blank" rel="noopener noreferrer">
                {source.label}
              </a>
            ))}
          </div>
        </div>
      </details>

      <Link
        className={styles.guideLink}
        href={`/guides/${species.slug}`}
        onClick={() => trackEvent('grass_atlas_guide_clicked', { species: species.slug })}
      >
        <span className={styles.guideIcon} aria-hidden="true">
          <BookOpenText size={19} />
        </span>
        <span className={styles.guideCopy}>
          <small>Next: Care guide</small>
          <strong>Learn how to grow {species.name}</strong>
          <em>Mowing · Watering · Lawn problems</em>
        </span>
        <span className={styles.guideAction}>
          Read guide
          <ArrowRight size={16} />
        </span>
      </Link>
    </aside>
  )
}

function ComparePanel({
  species,
  allSpecies,
}: {
  species: AtlasSpecies
  allSpecies: AtlasSpecies[]
}) {
  const initialLookalike = species.lookalikes[0]?.grass
  const defaultOther =
    allSpecies.find((item) => item.grassKey === initialLookalike) ??
    allSpecies.find((item) => item.slug !== species.slug) ??
    species
  const [otherSlug, setOtherSlug] = useState(defaultOther.slug)
  const other = allSpecies.find((item) => item.slug === otherSlug) ?? defaultOther
  const discriminator = species.lookalikes.find((item) => item.grass === other.grassKey)?.tell
  const speciesProfile = getAtlasMorphology(species.slug)
  const otherProfile = getAtlasMorphology(other.slug)

  return (
    <aside className={styles.detailPanel} aria-label={`${species.name} versus ${other.name} comparison`}>
      <span className={styles.eyebrow}>Compare grasses</span>
      <h2 className={styles.panelTitle}>{species.name} versus {other.name}</h2>
      <p className={styles.panelIntro}>Start with the blade tip, blade width, and how each grass spreads. These clues still work when the color changes.</p>

      <label className={styles.comparePicker}>
        <span>Compare {species.name} with</span>
        <div>
          <select value={other.slug} onChange={(event) => {
            setOtherSlug(event.target.value)
            trackEvent('grass_atlas_compare_selected', { species: species.slug, compared_with: event.target.value })
          }}>
            {allSpecies
              .filter((item) => item.slug !== species.slug)
              .map((item) => (
                <option value={item.slug} key={item.slug}>
                  {item.name}
                </option>
              ))}
          </select>
          <ChevronDown size={16} aria-hidden="true" />
        </div>
      </label>

      <div className={styles.verdict}>
        <p>
          <strong>Biggest difference</strong>
          {discriminator ?? `${species.name} has ${species.bladeWidth.toLowerCase()} blades. ${other.name} has ${other.bladeWidth.toLowerCase()} blades. Compare blade width and tip shape first, then how each one spreads.`}
        </p>
      </div>

      <div className={styles.compareGrid}>
        {[
          ['Blade width', species.bladeWidth, other.bladeWidth],
          ['Blade tip', species.bladeShape, other.bladeShape],
          ['How it spreads', species.growthHabit, other.growthHabit],
          ['Blade feel', species.texture, other.texture],
          ['New leaf shape', speciesProfile.vernation, otherProfile.vernation],
          ['Where blade meets stem', speciesProfile.collarCue, otherProfile.collarCue],
          ['Seedhead', species.seedhead, other.seedhead],
        ].map(([label, left, right]) => (
          <div className={styles.compareRow} key={label}>
            <span>{label}</span>
            <p>{left}</p>
            <p>{right}</p>
          </div>
        ))}
      </div>

      <div className={styles.compareSources}>
        <strong>Sources</strong>
        {[...speciesProfile.sources, ...otherProfile.sources]
          .filter((source, index, list) => list.findIndex((item) => item.href === source.href) === index)
          .map((source) => <a href={source.href} key={source.href} target="_blank" rel="noopener noreferrer">{source.publisher}</a>)}
      </div>
    </aside>
  )
}

interface AtlasQuizQuestion {
  question: string
  choices: Array<{ text: string; correct: boolean; why: string }>
}

function questionsForSpecies(species: AtlasSpecies, allSpecies: AtlasSpecies[]): AtlasQuizQuestion[] {
  const profile = getAtlasMorphology(species.slug)
  const lookalikeSpecies = species.lookalikes
    .map((lookalike) => allSpecies.find((item) => item.grassKey === lookalike.grass))
    .filter((item): item is AtlasSpecies => Boolean(item))
  const otherSpecies = [
    ...lookalikeSpecies,
    ...allSpecies.filter((item) => item.slug !== species.slug && !lookalikeSpecies.some((match) => match.slug === item.slug)),
  ]
  const otherProfiles = otherSpecies.map((item) => getAtlasMorphology(item.slug))
  const lookalike = species.lookalikes[0]
  const distinctAlternatives = (values: string[], answer: string) => {
    const seen = new Set([answer.trim().toLowerCase()])
    return values.filter((value) => {
      const normalized = value.trim().toLowerCase()
      if (seen.has(normalized)) return false
      seen.add(normalized)
      return true
    })
  }
  const bladeShapeAlternatives = distinctAlternatives(otherSpecies.map((item) => item.bladeShape), species.bladeShape)
  const collarAlternatives = distinctAlternatives(otherProfiles.map((item) => item.collarCue), profile.collarCue)
  const runnerAlternatives = distinctAlternatives(otherProfiles.map((item) => item.runnerCue), profile.runnerCue)
  const seedheadAlternatives = distinctAlternatives(otherSpecies.map((item) => item.seedhead), species.seedhead)

  return [
    {
      question: `What does the tip of ${species.name} look like?`,
      choices: [
        { text: species.bladeShape, correct: true, why: 'That tip matches the model. Use it with other clues before deciding which grass you have.' },
        { text: bladeShapeAlternatives[0] ?? 'The darkest green blade', correct: false, why: 'That tip belongs to a different grass. Look closely at the very end of the blade.' },
        { text: bladeShapeAlternatives[1] ?? 'Any blade growing nearby', correct: false, why: 'That shape does not match this model. The blade tip is more useful than color or location.' },
      ],
    },
    {
      question: 'What should you look for where the blade meets the stem?',
      choices: [
        { text: collarAlternatives[0] ?? 'No collar structures at all', correct: false, why: 'That belongs to a different grass. Bend the blade back where it meets the stem and take a close-up photo.' },
        { text: profile.collarCue, correct: true, why: 'The little collar parts stay the same even when two grasses look alike from far away.' },
        { text: 'The darkest green tissue in the lawn', correct: false, why: 'Color changes with water and food. The collar is a better clue.' },
      ],
    },
    {
      question: `How does ${species.name} spread?`,
      choices: [
        { text: 'It grows fastest after fertilizer', correct: false, why: 'How fast it grows changes with weather and care. Follow a real runner instead.' },
        { text: runnerAlternatives[0] ?? 'Every shoot has a surface runner', correct: false, why: 'That is how a different kind of grass spreads.' },
        { text: profile.runnerCue, correct: true, why: 'Where the runners go is a clue you can use all year.' },
      ],
    },
    {
      question: `Which seedhead belongs to ${species.name}?`,
      choices: [
        { text: seedheadAlternatives[0] ?? 'Any green seedhead', correct: false, why: 'Each grass has its own seedhead shape. Look at how the seeds attach to the branches.' },
        { text: species.seedhead, correct: true, why: 'A grown seedhead tells you more than the color of the lawn.' },
        { text: 'Seedheads do not matter once a lawn is mowed', correct: false, why: 'Mowing cuts most seedheads off, but a whole one is a great clue.' },
      ],
    },
    {
      question: lookalike ? `What is the easiest way to tell it apart from ${lookalike.grass}?` : 'What is the safest way to identify it?',
      choices: [
        { text: lookalike?.tell ?? 'Confirm blade, collar, growth habit, and seedhead together', correct: true, why: 'That uses a real part of the plant and a side-by-side check, not just how it looks.' },
        { text: 'Choose whichever grass is more common nearby', correct: false, why: 'Location narrows possibilities, but it does not replace physical evidence.' },
        { text: 'Use one lawn-wide color photo', correct: false, why: 'One faraway photo is too small to show the blade, collar, runner, or seedhead.' },
      ],
    },
  ]
}

const GRASS_BURST_BLADES = [
  { x: -96, y: -56, turn: -44, delay: 20, scale: 0.74 },
  { x: -78, y: -82, turn: -31, delay: 55, scale: 0.92 },
  { x: -58, y: -67, turn: -24, delay: 0, scale: 0.8 },
  { x: -40, y: -96, turn: -17, delay: 70, scale: 1.05 },
  { x: -22, y: -76, turn: -10, delay: 30, scale: 0.86 },
  { x: -8, y: -106, turn: -4, delay: 10, scale: 1.12 },
  { x: 8, y: -88, turn: 5, delay: 80, scale: 0.8 },
  { x: 23, y: -104, turn: 11, delay: 35, scale: 1.08 },
  { x: 39, y: -78, turn: 18, delay: 5, scale: 0.88 },
  { x: 56, y: -94, turn: 25, delay: 65, scale: 1 },
  { x: 73, y: -70, turn: 32, delay: 25, scale: 0.78 },
  { x: 92, y: -58, turn: 43, delay: 75, scale: 0.9 },
  { x: -68, y: -48, turn: -35, delay: 95, scale: 0.68 },
  { x: 66, y: -50, turn: 36, delay: 105, scale: 0.7 },
] as const

function GrassBurst() {
  return (
    <div className={styles.grassBurst} aria-hidden="true">
      {GRASS_BURST_BLADES.map((blade, index) => (
        <span
          key={`${blade.x}-${blade.y}`}
          style={{
            '--grass-burst-x': `${blade.x}px`,
            '--grass-burst-y': `${blade.y}px`,
            '--grass-burst-turn': `${blade.turn}deg`,
            '--grass-burst-delay': `${blade.delay}ms`,
            '--grass-burst-scale': blade.scale,
          } as CSSProperties}
          data-blade={index + 1}
        />
      ))}
    </div>
  )
}

function QuizPanel({ species, allSpecies }: { species: AtlasSpecies; allSpecies: AtlasSpecies[] }) {
  const [questionIndex, setQuestionIndex] = useState(0)
  const [selected, setSelected] = useState<number | null>(null)
  const [revealed, setRevealed] = useState(false)
  const [score, setScore] = useState(0)
  const questions = useMemo(() => questionsForSpecies(species, allSpecies), [species, allSpecies])
  const quiz = questions[questionIndex]
  const correct = quiz.choices.findIndex((choice) => choice.correct)
  const isLast = questionIndex === questions.length - 1

  function advanceQuiz() {
    if (isLast) {
      trackEvent('grass_atlas_quiz_completed', { species: species.slug, score, total: questions.length })
      setQuestionIndex(0)
      setSelected(null)
      setRevealed(false)
      setScore(0)
      return
    }
    setQuestionIndex((value) => value + 1)
    setSelected(null)
    setRevealed(false)
  }

  return (
    <aside className={styles.detailPanel} aria-label={`${species.name} field quiz`}>
      <div className={styles.quizMeta}>
        <span className={styles.eyebrow}>{species.name} quiz · {questionIndex + 1} of {questions.length}</span>
        <span>{score} right so far</span>
      </div>
      <div className={styles.quizProgress}>
        <span style={{ width: `${((questionIndex + (revealed ? 1 : 0)) / questions.length) * 100}%` }} />
      </div>
      <h2 className={styles.panelTitle}>{quiz.question}</h2>

      <div className={styles.quizOptions}>
        {quiz.choices.map((option, index) => {
          const showCorrect = revealed && index === correct
          const showWrong = revealed && selected === index && index !== correct
          return (
            <button
              type="button"
              key={`${questionIndex}-${index}`}
              className={`${selected === index ? styles.quizSelected : ''} ${showCorrect ? styles.quizCorrect : ''} ${showWrong ? styles.quizWrong : ''}`}
              disabled={revealed}
              onClick={() => {
                setSelected(index)
                setRevealed(true)
                if (index === correct) setScore((value) => value + 1)
              }}
            >
              <span>{showCorrect ? <Check size={14} /> : showWrong ? <X size={14} /> : String.fromCharCode(65 + index)}</span>
              <p>{option.text}</p>
              {showCorrect ? <Check size={18} /> : null}
              {showWrong ? <X size={18} /> : null}
            </button>
          )
        })}
      </div>

      {revealed ? (
        <>
          <div className={styles.quizFeedback}>
            {selected === correct ? <GrassBurst key={questionIndex} /> : null}
            <div className={selected === correct ? styles.answerGood : styles.answerTryAgain} aria-live="polite">
              <strong>{selected === correct ? 'Correct!' : 'Not quite.'}</strong>
              <p>{selected === null ? '' : quiz.choices[selected].why}</p>
            </div>
          </div>
          {isLast ? (
            <p className={styles.quizResult}>You got {score} of {questions.length}.</p>
          ) : null}
          <button
            type="button"
            className={styles.quizNext}
            onClick={advanceQuiz}
          >
            {isLast ? 'Play again' : 'Next question'}
          </button>
        </>
      ) : null}
    </aside>
  )
}

const PHONE_VIEWPORT_QUERY = '(max-width: 720px)'

function subscribePhoneViewport(onChange: () => void) {
  const media = window.matchMedia(PHONE_VIEWPORT_QUERY)
  media.addEventListener('change', onChange)
  return () => media.removeEventListener('change', onChange)
}

function usePhoneViewport() {
  return useSyncExternalStore(
    subscribePhoneViewport,
    () => window.matchMedia(PHONE_VIEWPORT_QUERY).matches,
    () => false,
  )
}

export function GrassAtlasExperience({ species, initialSlug }: { species: AtlasSpecies[]; initialSlug: string }) {
  const router = useRouter()
  const [selectedSlug, setSelectedSlug] = useState(initialSlug)
  const [mode, setMode] = useState<AtlasMode>('explore')
  const [activePart, setActivePart] = useState<AnatomyPart>('blade-tip')
  const [focusMode, setFocusMode] = useState(false)
  const isPhoneViewport = usePhoneViewport()
  const [numbersPreference, setNumbersPreference] = useState<boolean | null>(null)
  const numbersVisible = numbersPreference ?? !isPhoneViewport
  const [simulationInput, setSimulationInput] = useState<GrassSimulationInput>({
    day: SIMULATION_OPENING_DAY,
    water: 'right',
    mowing: 'healthy',
    nutrients: 'balanced',
  })
  const [simulationPlaying, setSimulationPlaying] = useState(false)
  const simulationCompletedRef = useRef(false)
  const entryTrackedRef = useRef(false)

  useEffect(() => {
    if (entryTrackedRef.current) return
    entryTrackedRef.current = true

    const requestedSource = new URLSearchParams(window.location.search).get('source')
    const knownSources = ['header', 'footer', 'tools', 'guide', 'identify_result']
    const source = requestedSource && knownSources.includes(requestedSource)
      ? requestedSource
      : requestedSource
        ? 'other'
        : 'direct'

    trackEvent('grass_atlas_entry_viewed', {
      source,
      species: initialSlug,
    })
  }, [initialSlug])

  useEffect(() => {
    setSelectedSlug(initialSlug)
    setActivePart('blade-tip')
    setSimulationInput((current) => ({ ...current, day: SIMULATION_OPENING_DAY }))
    setSimulationPlaying(false)
    simulationCompletedRef.current = false
  }, [initialSlug])

  useEffect(() => {
    if (!focusMode) return
    const previousOverflow = document.body.style.overflow
    const siteFooter = document.getElementById('site-footer')
    const previousFooterVisibility = siteFooter?.style.visibility ?? ''
    document.body.style.overflow = 'hidden'
    if (siteFooter) siteFooter.style.visibility = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
      if (siteFooter) siteFooter.style.visibility = previousFooterVisibility
    }
  }, [focusMode])

  const selectedSpecies = species.find((item) => item.slug === selectedSlug) ?? species[0]!
  const simulationVisualState = useMemo(
    () => getGrassSimulationState(simulationInput, selectedSpecies.simulationBaseline),
    [selectedSpecies.simulationBaseline, simulationInput],
  )
  const specimenSimulation = mode === 'simulate'
    ? simulationVisualState
    : getMatureHealthyState(selectedSpecies.simulationBaseline)

  useEffect(() => {
    if (mode !== 'simulate' || !simulationPlaying) return
    const timer = window.setInterval(() => {
      setSimulationInput((current) => {
        const nextDay = Math.min(90, current.day + 1)
        if (nextDay >= 90) setSimulationPlaying(false)
        return { ...current, day: nextDay }
      })
    }, 80)
    return () => window.clearInterval(timer)
  }, [mode, simulationPlaying])

  useEffect(() => {
    if (mode !== 'simulate' || simulationInput.day < 90 || simulationCompletedRef.current) return
    simulationCompletedRef.current = true
    trackEvent('grass_atlas_simulation_completed', {
      species: selectedSpecies.slug,
      day: 90,
      water: simulationInput.water,
      mowing: simulationInput.mowing,
      nutrients: simulationInput.nutrients,
    })
  }, [mode, selectedSpecies.slug, simulationInput])

  function trackSpeciesSelection(slug: string) {
    trackEvent('grass_atlas_species_selected', { species: slug, model_status: getAtlasMorphology(slug).status })
  }

  const selectedProfile = getAtlasMorphology(selectedSpecies.slug)

  function inspectPart(part: AnatomyPart) {
    setActivePart(part)
    if (mode !== 'quiz') setMode('explore')
    trackEvent('grass_atlas_anatomy_opened', { species: selectedSpecies.slug, anatomy_part: part, model_status: selectedProfile.status })
  }

  function selectMode(nextMode: AtlasMode) {
    setMode(nextMode)
    setSimulationPlaying(false)
    trackEvent('grass_atlas_mode_selected', { mode: nextMode, species: selectedSpecies.slug })
  }

  return (
    <div className={`${styles.pageShell} ${focusMode ? styles.focusMode : ''}`}>
      <section className={styles.intro}>
        <div>
          <span className={styles.introKicker}>
            <i aria-hidden="true" />
            Grass Atlas
          </span>
          <h1>Explore grass in 3D.</h1>
          <p>
            Choose a grass. Turn it to see its leaves, stems, roots, and seeds up close.
          </p>
        </div>
      </section>

      <section className={styles.atlasFrame} aria-label="Interactive grass atlas">
        <div className={styles.atlasToolbar}>
          <ModeTabs mode={mode} className={styles.desktopModeTabs} onModeChange={selectMode} />

          <label className={styles.grassSelectField}>
            <span>Choose grass</span>
            <div>
              <span className={styles.mobileGrassChoice} aria-hidden="true">
                <strong>{selectedSpecies.name}</strong>
                <small>{normalizeSeasonName(selectedSpecies.season)} · {selectedSpecies.zones}</small>
              </span>
              <select
                value={selectedSpecies.slug}
                aria-label="Choose grass"
                onChange={(event) => {
                  const slug = event.target.value
                  setSelectedSlug(slug)
                  setActivePart('blade-tip')
                  setSimulationInput((current) => ({ ...current, day: SIMULATION_OPENING_DAY }))
                  setSimulationPlaying(false)
                  simulationCompletedRef.current = false
                  trackSpeciesSelection(slug)
                  router.push(`/grass-atlas/${slug}`)
                }}
              >
                {species.map((item) => (
                  <option key={item.slug} value={item.slug}>
                    {item.name} · {normalizeSeasonName(item.season)} · {item.zones}
                  </option>
                ))}
              </select>
              <ChevronDown size={15} aria-hidden="true" />
            </div>
          </label>

          <div className={styles.utilityActions}>
            <button
              type="button"
              className={styles.mobileFocusAction}
              aria-pressed={focusMode}
              onClick={() => {
                const next = !focusMode
                setFocusMode(next)
                trackEvent('grass_atlas_focus_mode_changed', { species: selectedSpecies.slug, enabled: next })
              }}
            >
              {focusMode ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              {focusMode ? 'Back to lesson' : 'Full screen'}
            </button>
          </div>
        </div>

        <div className={styles.atlasGrid}>
          <div className={styles.viewer}>
            {mode !== 'simulate' ? (
              <button
                type="button"
                className={styles.numberToggle}
                aria-pressed={!numbersVisible}
                onClick={() => {
                  const next = !numbersVisible
                  setNumbersPreference(next)
                  trackEvent('grass_atlas_numbers_visibility_changed', {
                    species: selectedSpecies.slug,
                    visible: next,
                  })
                }}
              >
                {numbersVisible ? <EyeOff size={15} aria-hidden="true" /> : <Eye size={15} aria-hidden="true" />}
                {numbersVisible ? 'Hide numbers' : 'Show numbers'}
              </button>
            ) : null}
            <Specimen
              species={selectedSpecies}
              activePart={activePart}
              onPartChange={inspectPart}
              dissected
              simulation={specimenSimulation}
              simulationActive={mode === 'simulate'}
              showNumbers={numbersVisible}
            />
          </div>

          {mode === 'explore' ? (
            <ExplorePanel
              species={selectedSpecies}
              activePart={activePart}
              onPartChange={inspectPart}
              showNumbers={numbersVisible}
            />
          ) : null}
          {mode === 'compare' ? (
            <ComparePanel
              key={selectedSpecies.slug}
              species={selectedSpecies}
              allSpecies={species}
            />
          ) : null}
          {mode === 'quiz' ? <QuizPanel key={selectedSpecies.slug} species={selectedSpecies} allSpecies={species} /> : null}
          {mode === 'simulate' ? (
            <GrassSimulationPanel
              species={selectedSpecies}
              input={simulationInput}
              state={simulationVisualState}
              playing={simulationPlaying}
              onPlayingChange={(playing) => {
                if (playing) {
                  const day = simulationInput.day >= 90 ? 0 : simulationInput.day
                  if (day === 0) setSimulationInput((current) => ({ ...current, day: 0 }))
                  simulationCompletedRef.current = false
                  trackEvent('grass_atlas_simulation_played', {
                    species: selectedSpecies.slug,
                    day,
                    water: simulationInput.water,
                    mowing: simulationInput.mowing,
                    nutrients: simulationInput.nutrients,
                  })
                }
                setSimulationPlaying(playing)
              }}
              onDayChange={(day) => {
                setSimulationPlaying(false)
                setSimulationInput((current) => ({ ...current, day }))
                simulationCompletedRef.current = day >= 90
              }}
              onDayCommit={(day) => trackEvent('grass_atlas_simulation_changed', {
                species: selectedSpecies.slug,
                day,
                water: simulationInput.water,
                mowing: simulationInput.mowing,
                nutrients: simulationInput.nutrients,
              })}
              onPresetSelect={(preset: GrassSimulationPreset) => {
                const nextInput: GrassSimulationInput = {
                  day: preset.previewDay,
                  water: preset.water,
                  mowing: preset.mowing,
                  nutrients: preset.nutrients,
                }
                setSimulationPlaying(false)
                setSimulationInput(nextInput)
                simulationCompletedRef.current = false
                trackEvent('grass_atlas_simulation_changed', {
                  species: selectedSpecies.slug,
                  day: preset.previewDay,
                  water: preset.water,
                  mowing: preset.mowing,
                  nutrients: preset.nutrients,
                  preset: preset.id,
                })
              }}
              onConditionChange={(change) => {
                setSimulationPlaying(false)
                setSimulationInput((current) => ({ ...current, ...change }))
                simulationCompletedRef.current = false
                trackEvent('grass_atlas_simulation_changed', {
                  species: selectedSpecies.slug,
                  day: simulationInput.day,
                  water: change.water ?? simulationInput.water,
                  mowing: change.mowing ?? simulationInput.mowing,
                  nutrients: change.nutrients ?? simulationInput.nutrients,
                })
              }}
            />
          ) : null}
        </div>

        <ModeTabs mode={mode} className={styles.mobileModeTabs} onModeChange={selectMode} />
      </section>

      <section className={styles.takeawayStrip}>
        <div>
          <ShieldCheck size={20} />
          <p>
            <strong>A photo is a good start, not an answer.</strong>
            Check the blade, collar, runners, and seedhead before anyone buys seed or puts a product on the lawn.
          </p>
        </div>
        <Link href="/#analysis-card" onClick={() => trackEvent('grass_atlas_identify_cta_clicked', { species: selectedSpecies.slug })}>
          Identify my grass
          <ArrowRight size={17} />
        </Link>
      </section>
    </div>
  )
}
